import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Toast } from 'primereact/toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Phase,
  readAllPhases
} from '../../../Helper/ApiHelper/KeyValueNetworkHelper';
import {
  CatalogProject,
  CopyCatalogEP,
  copyExternalEpCatalogVersionToPhase,
  EP,
  EpPosition,
  EpTreeNode,
  generateEpTreeFromPositions,
  buildEpTaggingIndex,
  MinimizedEpCatalog,
  PatchEpPositionsTagsRequest,
  patchEpPositionTagsForPhase,
  readAllEpCatalogsFromPlatform,
  readEpCatalogForPhase,
  readEpCatalogMetaForAllPhases
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { errorMsgToString } from '../../../Helper/ApiHelper/ErrorMessages';
import BeeIconButton from '../../Atoms/BeeIconButton';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import AlphaEpCatalogSelectDialog from '../../Molecules/Dialogs/AlphaEpCatalogSelectDialog';
import AlphaEpCatalogTaggingDialog from '../../Molecules/Dialogs/AlphaEpCatalogTaggingDialog';
import './AlphaSetupTabLvVerwaltung.scss';
import { formatDate } from '../../../Helper/Util/DateHelper';
import { handlePromisesAndIgnoreErrors } from '../../../Helper/Util/PromiseHelper';
import { EpCatalogPickerSelection } from '../../Molecules/Dialogs/AlphaEpCatalogPickerStep';
import {
  EMPTY_FIELD_PLACEHOLDER,
  LeistungsSlices
} from '../../../Helper/Statics/Constants';

export function AlphaSetupTabLvVerwaltung() {
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(false);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [epLookup, setEpLookup] = useState<Map<string, EpTreeNode[] | null>>(
    new Map()
  );
  const [epDialogVisible, setEpDialogVisible] = useState<boolean>(false);
  const [epDialogPhase, setEpDialogPhase] = useState<Phase | undefined>();
  const [epDialogReadOnly, setEpDialogReadOnly] = useState<boolean>(false);
  const [tagDialogVisible, setTagDialogVisible] = useState<boolean>(false);
  const [tagDialogPhase, setTagDialogPhase] = useState<Phase | undefined>();
  const [tagDialogReadOnly, setTagDialogReadOnly] = useState<boolean>(false);
  const [allEpCatalogs, setAllEpCatalogs] = useState<CatalogProject[]>([]);
  const [epCompletenessByPhase, setEpCompletenessByPhase] = useState<
    Map<
      string,
      { isComplete: boolean; totalLeaves: number; totalTaggedLeaves: number }
    >
  >(() => new Map());
  //refs
  const toast = useRef<Toast>(null);

  ///////////////
  // USE EFFECTS //
  ///////////////
  useEffect(() => {
    setDataIsLoading(true);
    //load all liegenschaften

    readAllPhases()
      .then((phases: any) => {
        let phaseVals: Phase[] = [...phases];
        loadAllEpCatalogsForAllPhases(phaseVals);
      })
      .catch(() => {
        //ERROR phasen lesen fehler
        setDataIsLoading(false);
      })
      .finally(() => {
        setDataIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    readAllEpCatalogsFromPlatform()
      .then((catalogs) => {
        setAllEpCatalogs(catalogs);
      })
      .catch((err: unknown) => {
        toast.current?.show({
          severity: 'error',
          summary: 'EP-Kataloge konnten nicht geladen werden',
          detail: errorMsgToString(err)
        });
      })
      .finally(() => {
        setDataIsLoading(false);
      });
  }, []);

  const sliceTags = useMemo(() => {
    const sliceTagSet = new Set<string>();
    LeistungsSlices.forEach((slice) => {
      slice.tag.forEach((tag) => sliceTagSet.add(tag));
    });
    return Array.from(sliceTagSet);
  }, []);

  useEffect(() => {
    const result = new Map<
      string,
      { isComplete: boolean; totalLeaves: number; totalTaggedLeaves: number }
    >();
    epLookup.forEach((tree, phaseId) => {
      const index = buildEpTaggingIndex(tree, sliceTags);
      const isComplete =
        index.totalLeaves > 0 && index.totalTaggedLeaves === index.totalLeaves;
      const data = {
        isComplete,
        totalLeaves: index.totalLeaves,
        totalTaggedLeaves: index.totalTaggedLeaves
      };
      result.set(phaseId, data);
    });
    setEpCompletenessByPhase(result);
  }, [epLookup, sliceTags]);

  ///////////////
  // API CALLS //
  ///////////////

  function loadAllEpCatalogs(phases: Phase[]) {
    let epPromises: Promise<any>[] = [];
    phases.forEach((phase) => {
      epPromises.push(readEpCatalogForPhase(phase.id));
    });
    handlePromisesAndIgnoreErrors(epPromises).then((payload: any) => {
      let epCLookup = new Map<string, any>();
      if (payload && payload.fulfilled) {
        payload.fulfilled.forEach((c: any) => {
          if (c.value && c.value.data && c.value.info) {
            let currCatalog: EpTreeNode[] | null = generateEpTreeFromPositions(
              c.value.data
            );
            currCatalog = currCatalog ? currCatalog : [];

            epCLookup.set(c.value.info.phaseId, currCatalog);
          }
        });
      }
      setEpLookup(epCLookup);
      setDataIsLoading(false);
    });
  }

  function loadAllEpCatalogsForAllPhases(phases: Phase[]) {
    setDataIsLoading(true);
    //clean phases
    phases.forEach((p) => {
      p.lvs = [];
      p.ep = undefined;
    });
    readEpCatalogMetaForAllPhases()
      .then((epCatalogs: any) => {
        let epCats: MinimizedEpCatalog[] = [...epCatalogs];
        //search for phaseVals entry
        epCats.forEach((epE) => {
          if (epE && epE.phaseId) {
            //search for phaseVals entry
            for (let i = 0; i < phases.length; i++) {
              if (phases[i].id === epE.phaseId) {
                phases[i].ep = epE;
                break;
              }
            }
          }
        });
        loadAllEpCatalogs(phases);
        //finally set all data
        setPhases(phases);
      })
      .catch(() => {
        //Error loading EP-Catalog for Phases
        setDataIsLoading(false);
      });
  }

  function saveTagging(payload: PatchEpPositionsTagsRequest) {
    if (tagDialogReadOnly) {
      return;
    }

    if (!tagDialogPhase) {
      return;
    }
    const pid = tagDialogPhase.id;
    patchEpPositionTagsForPhase(pid, payload)
      .then((ep: EP) => {
        toast.current?.show({
          severity: 'success',
          summary: 'Tagging',
          detail: 'Leistungscluster wurden gespeichert.'
        });
        applyEpCatalogFromEpResponse(ep, pid);
        setTagDialogVisible(false);
      })
      .catch((err: unknown) => {
        toast.current?.show({
          severity: 'error',
          summary: 'Speichern fehlgeschlagen',
          detail: errorMsgToString(err)
        });
        return;
      });
  }
  ////////////////////
  // HELPER METHODS //
  ////////////////////
  function openEpCatalogDialog(phase: Phase, readOnly: boolean) {
    setEpDialogPhase(phase);
    setEpDialogReadOnly(readOnly);
    setEpDialogVisible(true);
  }

  function closeEpCatalogDialog() {
    setEpDialogVisible(false);
    setEpDialogPhase(undefined);
  }

  function openTagDialog(phase: Phase, readOnly: boolean) {
    setTagDialogPhase(phase);
    setTagDialogReadOnly(readOnly);
    setTagDialogVisible(true);
  }

  function closeTagDialog() {
    setTagDialogVisible(false);
    setTagDialogPhase(undefined);
  }
  // helper: determine if phase ended
  function isPhaseEnded(p: Phase) {
    if (!p || !p.end) return false;
    try {
      const endDate = new Date(p.end);
      return endDate.getTime() < new Date().getTime();
    } catch {
      return false;
    }
  }
  // create mock ep metadata if none exists (for UI/demo)
  function getEpMetaForPhase(p: Phase) {
    if (p.ep) {
      const ep = p.ep;
      const nameFromFile =
        ep.filename && ep.filename.length > 0
          ? ep.filename.replace(/\.[^/.]+$/, '')
          : '';
      return {
        id: ep.id,
        filename: ep.filename ?? EMPTY_FIELD_PLACEHOLDER,
        createdAt: ep.createdAt ?? new Date().toISOString(),
        name: ep.catalogName?.trim()
          ? ep.catalogName
          : nameFromFile || EMPTY_FIELD_PLACEHOLDER,
        version: ep.versionName?.trim()
          ? ep.versionName
          : EMPTY_FIELD_PLACEHOLDER,
        description:
          ep.description && ep.description.trim() !== ''
            ? ep.description
            : EMPTY_FIELD_PLACEHOLDER
      };
    }
  }

  ////////////////
  // UI METHODS //
  ////////////////

  function injectDescription() {
    return (
      <div className={'alpha-setup-tab-ep-verwaltung__description mb-3'}>
        Bitte verknüpfen Sie jede Phase mit einem entsprechenden
        EP-Leistungskatalog. Dieser kann entweder aus der Beestate-Plattform
        importiert oder aus der jeweils vorherigen Phase übernommen werden.
        Stellen Sie im weiteren Verlauf sicher, dass alle Leistungen den
        passenden Leistungsclustern zugeordnet sind. Eine vollständige und
        korrekte Cluster-Zuordnung ist erforderlich, um eine konsistente
        Auswertung und Weiterverarbeitung innerhalb der Anwendung zu
        gewährleisten.
      </div>
    );
  }
  function injectPhaseBody(p: Phase) {
    return (
      <div className={'alpha-setup-tab-ep-verwaltung__cell p-2'}>
        <div className={'alpha-setup-tab-ep-verwaltung__cell-title font-bold'}>
          {p.title}
        </div>
        <div className={'alpha-setup-tab-ep-verwaltung__cell-meta text-sm'}>
          {formatDate(p.start)} - {formatDate(p.end)}
        </div>
      </div>
    );
  }

  function injectEpCatalogBody(p: Phase) {
    const meta = getEpMetaForPhase(p);
    if (!meta) {
      return (
        <div className={'alpha-setup-tab-ep-verwaltung__cell p-2'}>
          <div
            className={'alpha-setup-tab-ep-verwaltung__cell-title font-bold'}
          >
            {EMPTY_FIELD_PLACEHOLDER}
          </div>
        </div>
      );
    }
    return (
      <div className={'alpha-setup-tab-ep-verwaltung__cell p-2'}>
        <div className={'alpha-setup-tab-ep-verwaltung__cell-title font-bold'}>
          {meta?.name}
        </div>
        <div className={'alpha-setup-tab-ep-verwaltung__cell-meta text-sm'}>
          Version: {meta?.version}
        </div>
        <div className={'alpha-setup-tab-ep-verwaltung__cell-meta text-sm'}>
          Erstellt: {formatDate(meta?.createdAt)}
        </div>
      </div>
    );
  }
  function injectCompleteBody(p: Phase) {
    const hasEpCatalog = !!p.ep;
    const treeLoaded = epLookup.has(p.id);
    const completenessData: {
      isComplete: boolean;
      totalLeaves: number;
      totalTaggedLeaves: number;
    } = epCompletenessByPhase.get(p.id) ?? {
      isComplete: false,
      totalLeaves: 0,
      totalTaggedLeaves: 0
    };

    const showCompletenessLoader = hasEpCatalog && !treeLoaded;

    return (
      <div className={'alpha-setup-tab-ep-verwaltung__cell p-2'}>
        {!hasEpCatalog ? (
          <div
            className={'alpha-setup-tab-ep-verwaltung__cell-title font-bold'}
          >
            {EMPTY_FIELD_PLACEHOLDER}
          </div>
        ) : showCompletenessLoader ? (
          <div
            className={
              'alpha-setup-tab-ep-verwaltung__cell-title font-bold flex align-items-center justify-content-start'
            }
          >
            <BeeLoadingSpinner strokeWidth={'2'} type={'secondary'} />
          </div>
        ) : (
          <div
            className={'alpha-setup-tab-ep-verwaltung__cell-title font-bold'}
          >
            {completenessData.isComplete
              ? 'Ja'
              : 'Nein' +
                ` (${completenessData.totalTaggedLeaves}/${completenessData.totalLeaves})`}
          </div>
        )}
      </div>
    );
  }

  function injectActionBody(p: Phase) {
    const ended = isPhaseEnded(p);
    const hasEp = !!p.ep;
    return (
      <div
        className={
          'alpha-setup-tab-ep-verwaltung__actions p-2 flex gap-2 align-items-center flex-wrap justify-content-start'
        }
      >
        {ended ? (
          <>
            <BeeIconButton
              iconClass={'pi pi-tags'}
              rounded={false}
              disabled={!hasEp}
              type={'secondary'}
              onClick={() => openTagDialog(p, true)}
            />
          </>
        ) : (
          <>
            {!epDialogReadOnly && (
              <BeeIconButton
                iconClass={'pi pi-pencil'}
                rounded={false}
                disabled={false}
                type={'primary'}
                onClick={() => openEpCatalogDialog(p, false)}
              />
            )}
            <BeeIconButton
              iconClass={'pi pi-tags'}
              rounded={false}
              disabled={!hasEp}
              type={'secondary'}
              onClick={() => openTagDialog(p, false)}
            />
          </>
        )}
      </div>
    );
  }

  function injectEpCatalogSelectDialog() {
    if (!epDialogVisible || !epDialogPhase) {
      return null;
    }
    const titleSuffix = epDialogPhase.title ?? '';
    return (
      <AlphaEpCatalogSelectDialog
        visible={epDialogVisible}
        catalogs={allEpCatalogs ?? []}
        readOnly={epDialogReadOnly}
        header={
          epDialogReadOnly
            ? 'EP-Katalog ansehen: ' + titleSuffix
            : 'EP-Katalog wählen: ' + titleSuffix
        }
        onHide={closeEpCatalogDialog}
        onConfirm={epDialogReadOnly ? undefined : selectEpCatalog}
      />
    );
  }

  function applyEpCatalogTreeAndMeta(
    phaseId: string,
    meta: MinimizedEpCatalog,
    positions: EpPosition[] | null | undefined
  ) {
    setPhases((prev) =>
      prev.map((p) => (p.id === phaseId ? { ...p, ep: meta } : p))
    );
    let currCatalog: EpTreeNode[] | null =
      generateEpTreeFromPositions(positions);
    currCatalog = currCatalog ? currCatalog : [];
    setEpLookup((prev) => {
      const next = new Map(prev);
      next.set(phaseId, currCatalog);
      return next;
    });
  }

  function applyEpCatalogFromCopyResponse(ep: CopyCatalogEP, phaseId: string) {
    const meta = ep.info;
    applyEpCatalogTreeAndMeta(phaseId, meta, ep.data ?? []);
    setEpDialogVisible(false);
  }

  function applyEpCatalogFromEpResponse(ep: EP, phaseId: string) {
    const meta: MinimizedEpCatalog = {
      ...ep.info,
      phaseId: ep.info?.phaseId ?? phaseId
    };
    applyEpCatalogTreeAndMeta(phaseId, meta, ep.data ?? []);
  }

  function selectEpCatalog(selection: EpCatalogPickerSelection) {
    if (!epDialogPhase) {
      return;
    }
    const phaseId = epDialogPhase.id;
    copyExternalEpCatalogVersionToPhase(
      phaseId,
      selection.epCatalogProjectId,
      selection.versionId
    )
      .then((ep: CopyCatalogEP) => {
        toast.current?.show({
          severity: 'success',
          summary: 'EP-Katalog',
          detail: 'Der EP-Katalog wurde der Phase zugewiesen.'
        });
        applyEpCatalogFromCopyResponse(ep, phaseId);
      })
      .catch((err: unknown) => {
        toast.current?.show({
          severity: 'error',
          summary: 'Zuweisung fehlgeschlagen',
          detail: errorMsgToString(err)
        });
      });
  }

  function injectEpCatalogTaggingDialog() {
    if (!tagDialogVisible || !tagDialogPhase) {
      return null;
    }
    const titleSuffix = tagDialogPhase.title ?? '';

    return (
      <AlphaEpCatalogTaggingDialog
        visible={tagDialogVisible}
        readOnly={tagDialogReadOnly}
        epCatalog={epLookup.get(tagDialogPhase.id)}
        tags={sliceTags}
        header={
          tagDialogReadOnly
            ? 'EP-Katalog zu Leistungsclustern zuordnen (Ansicht): ' +
              titleSuffix
            : 'EP-Katalog zu Leistungsclustern zuordnen: ' + titleSuffix
        }
        onHide={closeTagDialog}
        onConfirm={saveTagging}
      />
    );
  }

  return (
    <div className={'alpha-setup-tab-ep-verwaltung'}>
      <div>{injectDescription()}</div>
      {!dataIsLoading ? (
        <div className={'alpha-setup-tab-ep-verwaltung__table'}>
          <DataTable
            value={phases}
            scrollable
            scrollDirection="horizontal"
            emptyMessage={'Keine Phasen vorhanden'}
          >
            <Column
              header={'Phase'}
              headerClassName={'alpha-setup-tab-ep-verwaltung__col-phase'}
              className={'alpha-setup-tab-ep-verwaltung__col-phase'}
              body={(p: Phase) => {
                return injectPhaseBody(p);
              }}
            />
            <Column
              header={'EP-Katalog'}
              headerClassName={'alpha-setup-tab-ep-verwaltung__col-catalog'}
              className={'alpha-setup-tab-ep-verwaltung__col-catalog'}
              body={(p: Phase) => {
                return injectEpCatalogBody(p);
              }}
            />
            <Column
              header={'Vollständig'}
              headerClassName={'alpha-setup-tab-ep-verwaltung__col-complete'}
              className={'alpha-setup-tab-ep-verwaltung__col-complete'}
              body={(p: Phase) => {
                return injectCompleteBody(p);
              }}
            />
            <Column
              header={'Aktionen'}
              headerClassName={'alpha-setup-tab-ep-verwaltung__col-actions'}
              className={'alpha-setup-tab-ep-verwaltung__col-actions'}
              body={(p: Phase) => {
                return injectActionBody(p);
              }}
            />
          </DataTable>
        </div>
      ) : (
        <BeeLoadingSpinner strokeWidth={'3'} type={'secondary'} />
      )}

      {epDialogVisible && injectEpCatalogSelectDialog()}
      {tagDialogVisible && injectEpCatalogTaggingDialog()}
      <Toast ref={toast} position={'top-right'} />
    </div>
  );
}
